import React, { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, API, divisionOrder, divisionMeta, gradeLabel } from "@/lib/api";
import { Search, Check, Loader2, ArrowRight, ExternalLink, Upload, Download, RotateCcw, Calendar } from "lucide-react";
import { toast } from "sonner";

const GRADE_OPTIONS = [
  { code: "", label: "— (no grade)" },
  { code: "HS", label: "High School" },
  { code: "SR+", label: "College" },
  { code: "ADULT", label: "Adult" },
];

const escapeCsv = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function RosterPage() {
  const qc = useQueryClient();
  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.get("/groups")).data,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60_000,
  });
  const seasonYear = settings?.season_year || new Date().getFullYear();

  const [q, setQ] = useState("");
  const [divFilter, setDivFilter] = useState("All");
  // Track saving state per person: key = `${group.code}|${type}|${index}`
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [yearDraft, setYearDraft] = useState("");
  const [yearSaving, setYearSaving] = useState(false);
  const fileInputRef = useRef(null);

  // sync draft year with settings when they load
  React.useEffect(() => {
    if (settings?.season_year != null) setYearDraft(String(settings.season_year));
  }, [settings?.season_year]);

  const rows = useMemo(() => {
    const out = [];
    for (const g of groups) {
      if (divFilter !== "All" && g.division !== divFilter) continue;
      if (g.head_counselor) {
        out.push({ group: g, type: "head", index: 0, name: g.head_counselor, grade: "" });
      }
      (g.assistants || []).forEach((a, i) => {
        out.push({ group: g, type: "asst", index: i, name: a.name, grade: a.grade || "" });
      });
      (g.cits || []).forEach((c, i) => {
        out.push({ group: g, type: "cit", index: i, name: c.name, grade: c.grade || "" });
      });
    }
    const term = q.trim().toLowerCase();
    if (!term) return out;
    return out.filter((r) =>
      `${r.group.code} ${r.name} ${r.type}`.toLowerCase().includes(term)
    );
  }, [groups, divFilter, q]);

  // Persist a grade change on a single person via PATCH /api/groups/{code}
  const saveGrade = async (row, newGrade) => {
    const key = `${row.group.code}|${row.type}|${row.index}`;
    setSavingKey(key);
    try {
      const payload = {};
      if (row.type === "asst") {
        const next = [...(row.group.assistants || [])];
        next[row.index] = { ...next[row.index], grade: newGrade };
        payload.assistants = next;
      } else if (row.type === "cit") {
        const next = [...(row.group.cits || [])];
        next[row.index] = { ...next[row.index], grade: newGrade };
        payload.cits = next;
      } else {
        // head counselor has no grade in current model; ignore
        return;
      }
      await api.patch(`/groups/${row.group.code}`, payload);
      setSavedKey(key);
      setTimeout(() => setSavedKey(null), 1200);
      qc.invalidateQueries({ queryKey: ["groups"] });
    } catch (e) {
      toast.error(`Failed to save ${row.name}`);
    } finally {
      setSavingKey(null);
    }
  };

  const roleLabel = (t) => (t === "head" ? "Head" : t === "asst" ? "Asst." : "CIT");

  const totals = useMemo(() => {
    const t = {};
    for (const r of rows) t[r.grade || "blank"] = (t[r.grade || "blank"] || 0) + 1;
    return t;
  }, [rows]);

  const saveYear = async () => {
    const n = Number(yearDraft);
    if (!Number.isInteger(n) || n < 2000 || n > 2100) {
      toast.error("Enter a valid year (2000–2100)");
      return;
    }
    if (n === settings?.season_year) return;
    setYearSaving(true);
    try {
      await api.patch("/settings", { season_year: n });
      toast.success(`Season year set to ${n}`);
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch {
      toast.error("Could not save year");
    } finally {
      setYearSaving(false);
    }
  };

  const saveDeadline = async (key, value) => {
    if (!settings || value === settings[key]) return;
    try {
      await api.patch("/settings", { [key]: value });
      toast.success("Deadline saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch {
      toast.error("Could not save deadline");
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/groups/upload`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      setUploadResult(json);
      const okCount = json.updated?.length || 0;
      const skipCount = json.skipped?.length || 0;
      if (okCount > 0) toast.success(`Updated ${okCount} group${okCount === 1 ? "" : "s"}`);
      if (skipCount > 0) toast.warning(`Skipped ${skipCount} row${skipCount === 1 ? "" : "s"}`);
      if (okCount === 0 && skipCount === 0) toast.info("File was empty");
      qc.invalidateQueries({ queryKey: ["groups"] });
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const header = "Group Code,Role,Name,Grade\n";
    const body = [];
    for (const g of groups) {
      if (g.head_counselor) body.push(`${g.code},Head,${escapeCsv(g.head_counselor)},`);
      (g.assistants || []).forEach((a) => {
        body.push(`${g.code},Assistant,${escapeCsv(a.name)},${a.grade || ""}`);
      });
      (g.cits || []).forEach((c) => {
        body.push(`${g.code},CIT,${escapeCsv(c.name)},${c.grade || ""}`);
      });
    }
    const blob = new Blob([header + body.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `roster-${settings?.season_year || "current"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetRoster = async () => {
    if (!window.confirm(
      "Wipe every group's Head Counselor, Assistants, and CITs?\n\n" +
      "Group codes and divisions stay. Use this at the start of a new season, " +
      "then upload the new roster CSV/Excel."
    )) return;
    try {
      await api.post("/groups/reset");
      toast.success("Roster cleared — upload the new season's file next.");
      qc.invalidateQueries({ queryKey: ["groups"] });
      setUploadResult(null);
    } catch {
      toast.error("Reset failed");
    }
  };

  return (
    <div data-testid="roster-page">
      <div className="mb-6">
        <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
          Tip Letters
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B2545] mt-1">
          Roster &amp; Grades
        </h1>
        <p className="text-[#13315C]/75 mt-2 max-w-2xl text-sm sm:text-base">
          Every counselor across every group in one place. Change any grade
          from the dropdown — it saves automatically. Head Counselors don&apos;t
          use a grade code.
        </p>
      </div>

      {/* Season year + reset + upload panel */}
      <div className="paper-card p-4 sm:p-5 mb-6" data-testid="roster-season-panel">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-end">
          <div className="md:col-span-3">
            <div className="font-mono-camp text-[10px] tracking-widest text-[#13315C]/60 uppercase">
              Season year
            </div>
            <label className="block mt-1">
              <span className="sr-only">Season year</span>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#13315C]/40" />
                <input
                  data-testid="roster-year-input"
                  type="number"
                  min="2000"
                  max="2100"
                  value={yearDraft}
                  onChange={(e) => setYearDraft(e.target.value)}
                  onBlur={saveYear}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
                  className="w-full pl-9 pr-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-lg font-semibold text-[#0B2545] focus:outline-none focus:border-[#0B2545]"
                />
              </div>
            </label>
            <div className="text-[11px] text-[#13315C]/60 mt-1">
              {yearSaving ? "Saving…" : "Shown on every printable letter."}
            </div>
          </div>

          <div className="md:col-span-9">
            <div className="font-mono-camp text-[10px] tracking-widest text-[#13315C]/60 uppercase">
              New season workflow
            </div>
            <div className="font-display text-lg font-bold text-[#0B2545] leading-tight">
              Reset roster · Upload new file
            </div>
            <div className="text-xs text-[#13315C]/70 mt-1 mb-3">
              CSV or Excel · 4 columns: <b>Group Code</b>, <b>Role</b> (Head / Assistant / CIT),
              <b> Name</b>, <b>Grade</b> (blank, HS, SR+, or ADULT). Header row optional. Rows for
              unknown group codes are skipped.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="roster-reset"
                onClick={resetRoster}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#B54223] text-[#B54223] text-xs font-semibold hover:bg-[#B54223] hover:text-white"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Wipe roster
              </button>
              <button
                data-testid="roster-template"
                onClick={downloadTemplate}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E9DDC8] text-[#0B2545] text-xs font-semibold hover:border-[#0B2545]"
              >
                <Download className="w-3.5 h-3.5" /> Download current as CSV
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                data-testid="roster-upload-input"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
              <button
                data-testid="roster-upload-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0B2545] text-[#FDF6EC] text-xs font-semibold hover:bg-[#13315C] disabled:opacity-70"
              >
                {uploading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="w-3.5 h-3.5" /> Upload roster CSV / Excel</>
                )}
              </button>
            </div>
            {uploadResult && (
              <div className="mt-3 pt-3 border-t border-[#F1E7D3] text-xs" data-testid="roster-upload-result">
                <div className="text-[#0B2545]">
                  <b>{uploadResult.updated?.length || 0}</b> group{(uploadResult.updated?.length || 0) === 1 ? "" : "s"} updated
                  {(uploadResult.updated?.length || 0) > 0 && (
                    <span className="text-[#13315C]/70"> — {uploadResult.updated.join(", ")}</span>
                  )}
                </div>
                {(uploadResult.not_found_groups?.length || 0) > 0 && (
                  <div className="text-[#B54223] mt-0.5">
                    Not found: {uploadResult.not_found_groups.join(", ")}
                  </div>
                )}
                {(uploadResult.skipped?.length || 0) > 0 && (
                  <details className="mt-1 text-[#B54223]">
                    <summary className="cursor-pointer">
                      {uploadResult.skipped.length} row{uploadResult.skipped.length === 1 ? "" : "s"} skipped
                    </summary>
                    <ul className="mt-1 ml-4 list-disc space-y-0.5 text-[#13315C]/70 max-h-48 overflow-auto">
                      {uploadResult.skipped.map((s, i) => (
                        <li key={i}>Row {s.row}: {s.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Deadlines — inline editor */}
        <div className="mt-5 pt-5 border-t border-[#F1E7D3]" data-testid="roster-deadlines">
          <div className="font-mono-camp text-[10px] tracking-widest text-[#13315C]/60 uppercase mb-2">
            Letter deadlines (edit here → every letter updates)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <DeadlineField
              label="Last day of camp"
              defaultValue={settings?.last_day_of_camp || ""}
              testid="deadline-last-day"
              onSave={(v) => saveDeadline("last_day_of_camp", v)}
            />
            <DeadlineField
              label="Half Season 1 deadline"
              defaultValue={settings?.half_season_1_deadline || ""}
              testid="deadline-half-1"
              onSave={(v) => saveDeadline("half_season_1_deadline", v)}
            />
            <DeadlineField
              label="Full Season deadline"
              defaultValue={settings?.full_season_deadline || ""}
              testid="deadline-full"
              onSave={(v) => saveDeadline("full_season_deadline", v)}
            />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#13315C]/50" />
          <input
            data-testid="roster-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or group…"
            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#E9DDC8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2545]/25 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["All", ...divisionOrder].map((d) => (
            <button
              key={d}
              data-testid={`roster-filter-${d.toLowerCase()}`}
              onClick={() => setDivFilter(d)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                divFilter === d
                  ? "bg-[#0B2545] text-[#FDF6EC] border-[#0B2545]"
                  : "bg-white text-[#0B2545] border-[#E9DDC8] hover:border-[#0B2545]"
              }`}
            >
              {d === "All" ? "All" : divisionMeta[d].label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals bar */}
      <div className="flex flex-wrap gap-2 mb-4">
        {["SR+", "HS", "ADULT", "blank"].map((g) => (
          <span
            key={g}
            className="tag navy"
            data-testid={`roster-total-${
              g === "blank" ? "none" :
              g === "SR+" ? "college" :
              g === "HS" ? "hs" : "adult"
            }`}
          >
            {g === "blank" ? "No grade" : gradeLabel(g)}: {totals[g] || 0}
          </span>
        ))}
        <span className="tag navy">Total: {rows.length}</span>
      </div>

      {/* Roster table */}
      {isLoading ? (
        <div className="text-[#13315C]/60">Loading roster…</div>
      ) : (
        <div className="paper-card overflow-hidden">
          {/* Desktop header */}
          <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-3 text-[11px] font-mono-camp uppercase tracking-widest text-[#13315C]/60 border-b border-[#E9DDC8] bg-[#FBF7EF]">
            <div className="col-span-1">Group</div>
            <div className="col-span-1">Role</div>
            <div className="col-span-5">Name</div>
            <div className="col-span-4">Grade</div>
            <div className="col-span-1"></div>
          </div>

          {rows.length === 0 && (
            <div className="p-6 text-[#13315C]/60 italic">No matches.</div>
          )}

          {rows.map((row) => {
            const key = `${row.group.code}|${row.type}|${row.index}`;
            const isSaving = savingKey === key;
            const justSaved = savedKey === key;
            const isHead = row.type === "head";
            return (
              <div
                key={key}
                data-testid={`roster-row-${row.group.code}-${row.type}-${row.index}`}
                className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 border-b border-[#F1E7D3] last:border-b-0 hover:bg-[#FBFAF7]"
              >
                <div className="col-span-3 sm:col-span-1">
                  <Link
                    to={`/letter/group/${row.group.code}`}
                    className="inline-flex items-center gap-1 font-mono-camp text-xs font-semibold px-2 py-0.5 rounded bg-[#0B2545]/6 text-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC]"
                  >
                    {row.group.code} <ExternalLink className="w-2.5 h-2.5" />
                  </Link>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <span
                    className={`inline-block text-[10px] font-mono-camp uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      row.type === "head"
                        ? "bg-[#0B2545] text-[#FDF6EC]"
                        : row.type === "cit"
                        ? "bg-[#E76F51]/15 text-[#B54223]"
                        : "bg-[#E4A548]/20 text-[#7A5210]"
                    }`}
                  >
                    {roleLabel(row.type)}
                  </span>
                </div>
                <div className="col-span-7 sm:col-span-5 text-sm text-[#0B2545] truncate">
                  {row.name}
                </div>
                <div className="col-span-10 sm:col-span-4 mt-1 sm:mt-0">
                  {isHead ? (
                    <span className="text-xs italic text-[#13315C]/50">
                      Head counselors don&apos;t use a grade
                    </span>
                  ) : (
                    <select
                      data-testid={`roster-grade-${row.group.code}-${row.type}-${row.index}`}
                      value={row.grade || ""}
                      onChange={(e) => saveGrade(row, e.target.value)}
                      disabled={isSaving}
                      className="w-full px-2 py-1.5 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                    >
                      {GRADE_OPTIONS.map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  {isSaving && (
                    <Loader2 className="w-4 h-4 text-[#0B2545]/60 animate-spin" />
                  )}
                  {justSaved && (
                    <Check className="w-4 h-4 text-[#2A9D8F]" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 text-xs text-[#13315C]/60 max-w-2xl">
        Grade key: <b>College</b> = SR+ · <b>High School</b> = HS. Changes
        save automatically. To add / remove people or move someone between
        Assistant and CIT, use the <b>Edit</b> button on a group card in{" "}
        <Link to="/groups" className="underline">Groups</Link>.
      </div>
    </div>
  );
}

function DeadlineField({ label, defaultValue, testid, onSave }) {
  const [val, setVal] = React.useState(defaultValue);
  React.useEffect(() => { setVal(defaultValue); }, [defaultValue]);
  return (
    <label className="block">
      <span className="text-[10px] font-mono-camp uppercase tracking-widest text-[#13315C]/60">
        {label}
      </span>
      <input
        data-testid={testid}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => onSave(val)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="Friday, July 24"
        className="mt-0.5 w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm text-[#0B2545] focus:outline-none focus:border-[#0B2545]"
      />
    </label>
  );
}
